import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import EditNote from '@mui/icons-material/EditNote';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import GridOnIcon from '@mui/icons-material/GridOn';
import Info from '@mui/icons-material/Info';
import RedoIcon from '@mui/icons-material/Redo';
import SaveIcon from '@mui/icons-material/Save';
import TitleIcon from '@mui/icons-material/Title';
import UndoIcon from '@mui/icons-material/Undo';
import Warning from '@mui/icons-material/Warning';
import { Autocomplete, Box, Button, ButtonGroup, Divider, Fab, Grid, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Popover, Select, TextField, Tooltip, Typography } from '@mui/material';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useRef, useState } from 'react';
import { CitationExtension } from './CitationExtension';
import CopyToClipboardButton from './CopyToClipboardButton.js';
import { reportError } from './ErrorReporter';
import { getDiscoveryEvidenceFromAnalysis, getMedicalChronologyConsolidationState } from './PathUtils.js';
import { WarningCaveat, WarningNeedsReview } from './WarningExtensions';

export function hasUnresolvedNeedsReview(obj) {
    if (obj === null || typeof obj !== 'object') {
        return false;
    }
    if (Array.isArray(obj)) {
        return obj.some(item => hasUnresolvedNeedsReview(item));
    }
    // Recursively process all properties first
    Object.values(obj).some(item => hasUnresolvedNeedsReview(item));
    for (const key in obj) {
        if (key === 'needs_review' && Object.keys(obj[key]).length > 0) {
            return true;
        }
        if (typeof obj[key] === 'object' && hasUnresolvedNeedsReview(obj[key])) {
            return true;
        }
    }
    return false;
}

export default function Drafting({ caseInfo, setCaseInfo, setSnackBarMessage, setDisplayedDocument, fetchCaseInfoAndUpdateUI }) {
    const [selectedDraft, setSelectedDraft] = useState(null);
    const [htmlContent, setHtmlContent] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [selectedDraftType, setSelectedDraftType] = useState(null);
    // Track editor transactions to force re-render and update active button states
    // eslint-disable-next-line no-unused-vars
    const [selectionUpdate, setSelectionUpdate] = useState(0);

    const [isSmallViewport, setIsSmallViewport] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth <= 1366;
        }
        return false;
    });

    useEffect(() => {
        const handleResize = () => {
            setIsSmallViewport(window.innerWidth <= 1366);
        };

        window.addEventListener('resize', handleResize);
        // Check on mount in case initial state was wrong
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const draftTypes = [
        {
            label: 'Medical Timeline Narrartion',
            prompt: 'medical_timeline_narration',
            required_preconditions: [
                'atleast_one_medical_chronology_item',
                'no_unconsolidated_medical_chronology',
                'no_processing_in_progress'
            ],
            optional_preconditions: [
                'no_unresolved_needs_review_in_medical_chronology',
                'all_medical_records'
            ]
        },
        {
            label: 'Demand Letter',
            prompt: 'demand_letter',
            required_preconditions: [
                'atleast_one_medical_chronology_item',
                'no_unconsolidated_medical_chronology_or_medical_expenses',
                // 'incident_report_uploaded',
                'no_processing_in_progress'
            ],
            optional_preconditions: [
                'no_unresolved_needs_review_in_medical_chronology',
                'no_unresolved_needs_review_in_medical_expenses',
                'no_unresolved_needs_review_in_incident_details',
                'no_unresolved_needs_review_in_timeline',
                'no_unresolved_needs_review_in_discovery_evidence',
                'all_medical_records',
                'all_medical_bills'
            ]
            // },
            // {
            //     label: 'Work Excuse Timeline',
            //     prompt: 'work_excuse_timeline',
            //     required_preconditions: [
            //         'atleast_one_medical_chronology_item',
            //         'atleast_one_work_status_disability',
            //         'no_unconsolidated_medical_chronology',
            //         'no_processing_in_progress'
            //     ],
            //     optional_preconditions: [
            //         'no_unresolved_needs_review_in_medical_chronology',
            //         'all_medical_records'
            //     ]
        }
    ];

    const preconditionsMap = {
        no_processing_in_progress: {
            description: 'All processing is complete.',
            callback: (caseInfo) => {
                return !(caseInfo['analysis_queue']?.length > 0 || Object.values(caseInfo['documents'] || {}).some(doc => doc['analysis_queue']?.length > 0));
            }
        },
        atleast_one_medical_chronology_item: {
            description: 'Upload medical records using Secure Upload above.',
            callback: (caseInfo) => {
                return caseInfo.analysis?.medical_reports?.length > 0 || Object.values(caseInfo.documents).some(doc => doc.analysis?.medical_chronology?.length > 0);
            }
        },
        atleast_one_work_status_disability: {
            description: 'Work status or disability must be mentioned in the medical chronology.',
            callback: (caseInfo) => {
                return Object.values(caseInfo.documents).some(doc => doc.analysis?.medical_reports?.medical_chronology?.some(item => item.detailed_narrative?.some(narrative => narrative.item_type === 'work_status_disability' && narrative.value)));
            }
        },
        all_medical_records: {
            description: 'Upload all medical records for the case to ensure a complete draft. (Very few records found)',
            callback: (caseInfo) => {
                return caseInfo.analysis?.medical_reports?.[0]?.medical_chronology?.length > 20;
            }
        },
        all_medical_bills: {
            description: 'Upload all medical bills for the case to ensure the full claim amount is known. (Medical expenses are very low)',
            callback: (caseInfo) => {
                return caseInfo.analysis?.medical_reports?.[0]?.medical_expenses_summary?.total_charge > 2000;
            }
        },
        incident_report_uploaded: {
            description: 'Upload the police report using Secure Upload above.',
            callback: (caseInfo) => {
                return Object.values(caseInfo.documents).some(doc => doc.analysis?.incident_details?.length > 0);
            }
        },
        no_unconsolidated_medical_chronology: {
            description: 'Consolidate medical chronology on the Medical Chronology tab by clicking the "Consolidate" button to eliminate duplication and ensure a consistent draft.',
            callback: (caseInfo) => getMedicalChronologyConsolidationState(caseInfo).showConsolidateButton === false,
        },
        no_unconsolidated_medical_chronology_or_medical_expenses: {
            description: 'Consolidate medical information on the Medical Chronology or Medical Expenses tab by clicking the "Consolidate" button.',
            callback: (caseInfo) => getMedicalChronologyConsolidationState(caseInfo).showConsolidateButton === false,
        },
        no_unresolved_needs_review_in_discovery_evidence: {
            description: 'Resolve all warnings on the Discovery tab to ensure a high quality draft',
            callback: (caseInfo) => !Object.values(caseInfo.documents || {}).some((doc) =>
                hasUnresolvedNeedsReview(getDiscoveryEvidenceFromAnalysis(doc.analysis))
            ),
        },
        no_unresolved_needs_review_in_medical_chronology: {
            description: 'Resolve all warnings in the Medical Chronology tab to ensure a high quality draft',
            callback: (caseInfo) => !caseInfo.analysis?.medical_reports?.some(medReport => hasUnresolvedNeedsReview(medReport.medical_chronology || {}))
        },
        no_unresolved_needs_review_in_medical_expenses: {
            description: 'Resolve all warnings in the Medical Expenses tab to ensure accurate claim amount',
            callback: (caseInfo) => !caseInfo.analysis?.medical_reports?.some(medReport => hasUnresolvedNeedsReview(medReport.medical_expenses || {}))
        },
        no_unresolved_needs_review_in_incident_details: {
            description: 'Resolve all warnings in the Incident Details tab to ensure a high quality draft',
            callback: (caseInfo) => !Object.values(caseInfo.documents).some(doc => hasUnresolvedNeedsReview(doc.analysis?.incident_details))
        },
        no_unresolved_needs_review_in_timeline: {
            description: 'Resolve all warnings in the Timeline tab to ensure a high quality draft',
            callback: (caseInfo) => !Object.values(caseInfo.documents).some(doc => hasUnresolvedNeedsReview(doc.analysis?.timeline))
        }
    }

    // Ref to the scrollable editor container
    const editorScrollContainerRef = useRef(null);
    // Ref to the editor content for copying to clipboard
    const editorContentRef = useRef(null);
    // Ref to track polling timeout for cleanup
    const pollingTimeoutRef = useRef(null);
    // Ref to track AbortController for cancelling in-flight fetch requests
    const fetchAbortControllerRef = useRef(null);
    // Ref to track when we're updating editor from server (to prevent onUpdate from firing)
    const isUpdatingFromServerRef = useRef(false);
    // Ref to track debounced auto-save timer
    const autoSaveTimerRef = useRef(null);
    // Ref to store the latest handleSave function to avoid stale closures
    const handleSaveRef = useRef(null);

    // Warning popover state
    const [warningPopover, setWarningPopover] = useState({
        anchorEl: null,
        message: '',
        type: null, // 'needs_review' or 'caveat'
        scrollPosition: null // Store scroll position to prevent unwanted scrolling
    });

    // Table insert popover state
    const [tableInsertPopover, setTableInsertPopover] = useState({
        anchorEl: null,
        selectedRows: 3,
        selectedCols: 3,
        withHeaderRow: true
    });

    // Table context menu state
    const [tableContextMenu, setTableContextMenu] = useState({
        anchorEl: null,
        mouseX: null,
        mouseY: null
    });

    // Sync client generated selectedDraft with server data when caseInfo updates
    useEffect(() => {
        if (selectedDraft && caseInfo?.drafts) {
            const serverDraft = caseInfo.drafts.find(draft => draft.filename === selectedDraft.filename);
            if (serverDraft) {
                // Update selectedDraft to match server data (e.g., if display_name changed)
                // Only update if values actually changed to avoid unnecessary re-renders
                if (JSON.stringify(serverDraft) !== JSON.stringify(selectedDraft)) {
                    setSelectedDraft(serverDraft);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseInfo]);

    // Set the first draft as default selectedDraft when drafts are available
    useEffect(() => {
        if (!selectedDraft && caseInfo?.drafts && caseInfo.drafts.length > 0) {
            // Sort drafts by last_updated (most recent first) to match the UI sorting
            const sortedDrafts = [...caseInfo.drafts].sort((a, b) => (b.last_updated || 0) - (a.last_updated || 0));
            setSelectedDraft(sortedDrafts[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseInfo]);

    useEffect(() => {
        if (!caseInfo || !caseInfo.caseId || !selectedDraft) {
            setIsLoading(false);
            setHtmlContent('');
            // Clear any existing polling timeout
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
                pollingTimeoutRef.current = null;
            }
            // Cancel any in-flight fetch
            if (fetchAbortControllerRef.current) {
                fetchAbortControllerRef.current.abort();
                fetchAbortControllerRef.current = null;
            }
            return;
        }

        // Clear any existing polling timeout when draft changes
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }

        // Cancel any in-flight fetch to prevent multiple concurrent requests
        if (fetchAbortControllerRef.current) {
            fetchAbortControllerRef.current.abort();
            fetchAbortControllerRef.current = null;
        }

        const fetchDraft = async () => {
            // Create a new AbortController for this fetch
            const abortController = new AbortController();
            fetchAbortControllerRef.current = abortController;

            try {
                setIsLoading(true);
                setError(null);
                const response = await fetch(`/api/documents/${caseInfo.caseId}/${selectedDraft.filename}`, {
                    method: 'GET',
                    credentials: 'same-origin',
                    cache: 'no-cache',
                    signal: abortController.signal
                });

                // Check if the request was aborted
                if (abortController.signal.aborted) {
                    return;
                }

                if (!response.ok) {
                    const error = new Error(`Failed to load draft: ${response.statusText}`);
                    await reportError(error, 'Drafting.fetchDraft', {
                        caseId: caseInfo?.caseId,
                        filename: selectedDraft.filename,
                        status: response.status,
                        statusText: response.statusText
                    });
                    if (response.status === 404) {
                        setError('Draft document not found');
                    } else {
                        setError(`Failed to load draft: ${response.statusText}`);
                    }
                    setIsLoading(false);
                    fetchAbortControllerRef.current = null;
                    return;
                }

                const html = await response.text();

                // Check again if the request was aborted after async operations
                if (abortController.signal.aborted) {
                    return;
                }

                const isStillGenerating = html.includes('Draft is being generated...');

                // Always update htmlContent, even if still generating
                setHtmlContent(html);

                if (isStillGenerating) {
                    // Schedule next poll
                    pollingTimeoutRef.current = setTimeout(fetchDraft, 10000);
                }
                setIsLoading(false);
                fetchAbortControllerRef.current = null;
            } catch (err) {
                // Ignore AbortError - it's expected when cancelling requests
                if (err.name === 'AbortError') {
                    return;
                }

                // Check if the request was aborted before reporting error
                if (abortController.signal.aborted) {
                    return;
                }

                await reportError(err, 'Drafting.fetchDraft', {
                    caseId: caseInfo?.caseId,
                    filename: selectedDraft.filename,
                    responseStatus: err.response?.status
                });
                setError('Error loading draft document');
                setIsLoading(false);
                fetchAbortControllerRef.current = null;
            }
        };

        fetchDraft();

        // Cleanup function to clear timeout and cancel in-flight fetch on unmount or when dependencies change
        return () => {
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
                pollingTimeoutRef.current = null;
            }
            if (fetchAbortControllerRef.current) {
                fetchAbortControllerRef.current.abort();
                fetchAbortControllerRef.current = null;
            }
        };
    }, [caseInfo, selectedDraft]);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                link: false, // Disable default link to use our configured version
                underline: false, // Disable default underline to use our configured version
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            TableKit.configure({
                table: { resizable: true },
            }),
            Color,
            TextStyle,
            Highlight.configure({
                multicolor: true,
            }),
            CitationExtension.configure({
                openOnClick: false,
            }),
            WarningNeedsReview,
            WarningCaveat,
        ],
        content: htmlContent || '',
        editable: true,
        onUpdate: () => {
            // Only set unsaved changes for user edits, not server updates
            if (!isUpdatingFromServerRef.current) {
                setHasUnsavedChanges(true);
            }
        },
        onTransaction: () => {
            // Force re-render after any transaction to update active button states
            // This includes formatting changes, selection changes, and content changes
            setSelectionUpdate(prev => prev + 1);
        },
    });

    // Update editor content when HTML content changes
    useEffect(() => {
        if (editor && htmlContent) {
            const currentContent = editor.getHTML();

            // Only update if content actually changed
            if (currentContent !== htmlContent) {
                // htmlContent only changes from server fetches or after saving.
                // Use ref to prevent onUpdate from setting hasUnsavedChanges during server updates.
                isUpdatingFromServerRef.current = true;
                editor.commands.setContent(htmlContent);
                // Reset after update completes (onUpdate fires synchronously during setContent)
                isUpdatingFromServerRef.current = false;
            }
        }
    }, [editor, htmlContent]);

    // Handle citation clicks
    useEffect(() => {
        if (!editor || !caseInfo || !setDisplayedDocument) {
            return;
        }

        const handleClick = async (event) => {
            // Check if clicked element is a citation link
            const linkElement = event.target.closest('a.citation');
            if (!linkElement) {
                return;
            }

            const filename = linkElement.getAttribute('data-filename');
            const pageStr = linkElement.getAttribute('data-page');

            if (!filename) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            // Find document key from filename
            let docKey = null;
            for (const key in caseInfo['documents']) {
                if (caseInfo['documents'][key]['filename'] === filename) {
                    docKey = key;
                    break;
                }
            }

            if (docKey) {
                const page = pageStr ? parseInt(pageStr, 10) : 1;
                setDisplayedDocument({
                    'key': docKey,
                    'page': page
                });
            } else {
                const error = new Error(`Document not found for filename: ${filename}`);
                await reportError(error, 'Drafting.handleCitationClick', {
                    filename,
                    page: pageStr,
                    caseId: caseInfo?.caseId,
                    availableDocuments: Object.keys(caseInfo['documents'] || {}).length
                });
            }
        };

        const editorElement = editor.view.dom;
        editorElement.addEventListener('click', handleClick);

        return () => {
            editorElement.removeEventListener('click', handleClick);
        };
    }, [editor, caseInfo, setDisplayedDocument]);

    // Handle warning badge clicks (click position detection)
    useEffect(() => {
        if (!editor) {
            return;
        }

        const handleWarningClick = (event) => {
            // Check if clicked element is inside a warning span
            const warningSpan = event.target.closest('.warning-needs-review, .warning-caveat');
            if (!warningSpan) {
                return;
            }

            const rect = warningSpan.getBoundingClientRect();
            const clickX = event.clientX - rect.left;

            // Define click zones
            const badgeWidth = 28; // Badge width (18px) + margin (6px) + buffer (4px)
            const dismissButtonWidth = 7; // 18px button + 4px buffer - 15px right offset

            // Zone 1: Left side - Badge area (show popover)
            if (clickX < badgeWidth) {
                event.preventDefault();
                event.stopPropagation();

                // Store current scroll position to prevent unwanted scrolling
                const scrollContainer = editorScrollContainerRef.current;
                const scrollPosition = scrollContainer ? {
                    scrollTop: scrollContainer.scrollTop,
                    scrollLeft: scrollContainer.scrollLeft
                } : null;

                // Determine warning type from class
                const isNeedsReview = warningSpan.classList.contains('warning-needs-review');
                const type = isNeedsReview ? 'needs_review' : 'caveat';

                // Get message from title attribute
                const message = warningSpan.getAttribute('title') || '';

                setWarningPopover({
                    anchorEl: warningSpan,
                    message: message || 'No additional details available.',
                    type,
                    scrollPosition
                });
                return;
            }

            // Zone 2: Right side - Dismiss button area
            if (clickX > rect.width - dismissButtonWidth) {
                event.preventDefault();
                event.stopPropagation();

                // Determine warning type from class
                const isNeedsReview = warningSpan.classList.contains('warning-needs-review');
                const markName = isNeedsReview ? 'warningNeedsReview' : 'warningCaveat';

                try {
                    const view = editor.view;

                    // Get start position (beginning of the span)
                    const startPos = view.posAtDOM(warningSpan, 0);

                    // Get end position by finding the position after the span
                    // We'll use the span's text content length as a simple approach
                    const textLength = warningSpan.textContent?.length || 0;
                    const endPos = startPos + textLength;

                    // If we have valid positions, select and remove the mark
                    if (endPos > startPos) {
                        editor.chain()
                            .focus()
                            .setTextSelection({ from: startPos, to: endPos })
                            .unsetMark(markName)
                            .run();

                        setSnackBarMessage(`Warning removed`);
                    } else {
                        // Fallback: try to remove mark at current position
                        editor.chain()
                            .focus()
                            .setTextSelection(startPos)
                            .unsetMark(markName)
                            .run();
                        setSnackBarMessage(`Warning removed`);
                    }
                } catch (error) {
                    console.error('Error removing warning mark:', error);
                    setSnackBarMessage('Error removing warning');
                }
                return;
            }

            // Zone 3: Middle area - Let TipTap handle it for normal editing
            // (no action needed, TipTap will handle the click)
        };

        const editorElement = editor.view.dom;
        editorElement.addEventListener('click', handleWarningClick);

        return () => {
            editorElement.removeEventListener('click', handleWarningClick);
        };
    }, [editor, setSnackBarMessage]);

    // Handle right-click context menu for table editing
    useEffect(() => {
        if (!editor) {
            return;
        }

        const handleContextMenu = (event) => {
            // Check if cursor is in a table
            if (editor.isActive('table')) {
                event.preventDefault();
                event.stopPropagation();

                setTableContextMenu({
                    anchorEl: event.currentTarget,
                    mouseX: event.clientX,
                    mouseY: event.clientY
                });
            }
        };

        const handleClickOutside = (event) => {
            // Only close if clicking outside the menu
            const menuElement = document.querySelector('[role="menu"]');
            if (menuElement && !menuElement.contains(event.target)) {
                setTableContextMenu({
                    anchorEl: null,
                    mouseX: null,
                    mouseY: null
                });
            }
        };

        const editorElement = editor.view.dom;
        editorElement.addEventListener('contextmenu', handleContextMenu);
        // Use mousedown to close menu when clicking outside (before menu item click handlers fire)
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            editorElement.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [editor]);

    // Debounced auto-save: triggers 10 seconds after typing stops
    useEffect(() => {
        // Clear any existing timer
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }

        // Only set up auto-save if there are unsaved changes and conditions are met
        if (!hasUnsavedChanges || isSaving || !editor || !caseInfo?.caseId || !selectedDraft) {
            return;
        }

        // Set timer to auto-save after 10 seconds of inactivity
        autoSaveTimerRef.current = setTimeout(() => {
            // Check conditions again when timer fires (they may have changed)
            if (!isSaving && editor && caseInfo?.caseId && selectedDraft && handleSaveRef.current) {
                handleSaveRef.current(false); // false = auto-save, don't show message
            }
            autoSaveTimerRef.current = null;
        }, 10000); // 10 seconds

        // Cleanup function
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
                autoSaveTimerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasUnsavedChanges, isSaving, editor, caseInfo?.caseId, selectedDraft?.filename]);

    // Keyboard shortcut for save (Ctrl+S)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                if (hasUnsavedChanges && !isSaving && handleSaveRef.current) {
                    handleSaveRef.current(true); // true = manual save, show message
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [hasUnsavedChanges, isSaving]);

    // Auto-save on page unload/visibility change (safety net)
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            // Only trigger if there are unsaved changes
            if (hasUnsavedChanges && editor && caseInfo?.caseId && selectedDraft && !isSaving) {
                // Show browser warning
                e.preventDefault();
                e.returnValue = ''; // Required for Chrome

                // Attempt to save using sendBeacon for better reliability on page unload
                const html = editor.getHTML();
                const blob = new Blob([html], { type: 'text/html' });
                navigator.sendBeacon(
                    `/api/documents/${caseInfo.caseId}/${selectedDraft.filename}`,
                    blob
                );
            }
        };

        const handleVisibilityChange = () => {
            // Save when tab becomes hidden if there are unsaved changes
            if (document.hidden && hasUnsavedChanges && editor && caseInfo?.caseId && selectedDraft && !isSaving && handleSaveRef.current) {
                // Clear any pending debounced save and save immediately
                if (autoSaveTimerRef.current) {
                    clearTimeout(autoSaveTimerRef.current);
                    autoSaveTimerRef.current = null;
                }
                handleSaveRef.current();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasUnsavedChanges, editor, caseInfo?.caseId, selectedDraft?.filename, isSaving]);

    // Clear auto-save timer and unsaved changes when switching drafts
    useEffect(() => {
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }
        setHasUnsavedChanges(false);
    }, [selectedDraft?.filename]);

    const handleSave = async (isManual = false) => {
        if (!editor || !caseInfo?.caseId || !selectedDraft) {
            return;
        }

        // Clear any pending auto-save timer since we're saving manually
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }

        try {
            setIsSaving(true);
            const html = editor.getHTML();

            const response = await fetch(`/api/documents/${caseInfo.caseId}/${selectedDraft.filename}`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'text/html',
                },
                body: html,
            });

            if (!response.ok) {
                throw new Error(`Failed to save: ${response.statusText}`);
            }

            // Update htmlContent state to match what was saved
            setHtmlContent(html);
            setHasUnsavedChanges(false);

            // Only show snackbar message for manual saves
            if (isManual) {
                setSnackBarMessage('Draft saved successfully');
            }
        } catch (err) {
            await reportError(err, 'Drafting.handleSave', {
                caseId: caseInfo?.caseId,
                filename: selectedDraft?.filename,
                htmlLength: editor?.getHTML()?.length || 0
            });
            // Only show error message for manual saves
            if (isManual) {
                setSnackBarMessage('Error saving draft: ' + err.message);
            }
        } finally {
            setIsSaving(false);
        }
    };

    // Keep handleSave ref up to date
    useEffect(() => {
        handleSaveRef.current = handleSave;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, caseInfo?.caseId, selectedDraft?.filename, setHtmlContent, setHasUnsavedChanges, setIsSaving, setSnackBarMessage]);

    // Set editorContentRef to the .ProseMirror element for copying to clipboard
    useEffect(() => {
        if (!editor) {
            editorContentRef.current = null;
            return;
        }

        // Function to set the ref to the ProseMirror DOM element
        const setEditorContentRef = () => {
            // Use TipTap's direct API to get the DOM element (most reliable)
            // editor.view.dom is the ProseMirror DOM element
            if (editor.view?.dom) {
                editorContentRef.current = editor.view.dom;
                return true;
            }

            // Fallback: query for .ProseMirror element if view.dom is not available
            if (editorScrollContainerRef.current) {
                const proseMirrorElement = editorScrollContainerRef.current.querySelector('.ProseMirror');
                if (proseMirrorElement) {
                    editorContentRef.current = proseMirrorElement;
                    return true;
                }
            }

            return false;
        };

        // Try to set immediately
        if (setEditorContentRef()) {
            return;
        }

        // If not available yet, wait for next frame (DOM might not be ready)
        requestAnimationFrame(() => {
            setEditorContentRef();
        });
    }, [editor, selectedDraft?.filename, htmlContent]);

    // Get sorted drafts (reverse chronological order)
    const sortedDrafts = caseInfo?.drafts
        ? [...caseInfo.drafts].sort((a, b) => (b.last_updated || 0) - (a.last_updated || 0))
        : [];

    // Check if all required preconditions are met for the selected draft type
    const areAllRequirementsMet = () => {
        if (!selectedDraftType || !selectedDraftType.required_preconditions || selectedDraftType.required_preconditions.length === 0) {
            return false;
        }
        return selectedDraftType.required_preconditions.every((preconditionKey) => {
            const precondition = preconditionsMap[preconditionKey];
            return precondition && precondition.callback(caseInfo);
        });
    };

    // Count met requirements
    const getRequirementsStatus = () => {
        if (!selectedDraftType || !selectedDraftType.required_preconditions || selectedDraftType.required_preconditions.length === 0) {
            return { met: 0, total: 0 };
        }
        const met = selectedDraftType.required_preconditions.filter((preconditionKey) => {
            const precondition = preconditionsMap[preconditionKey];
            return precondition && precondition.callback(caseInfo);
        }).length;
        return { met, total: selectedDraftType.required_preconditions.length };
    };

    // Check if all recommendations are met
    const areAllRecommendationsMet = () => {
        if (!selectedDraftType || !selectedDraftType.optional_preconditions || selectedDraftType.optional_preconditions.length === 0) {
            return true; // If no recommendations, consider them all met
        }
        return selectedDraftType.optional_preconditions.every((preconditionKey) => {
            const precondition = preconditionsMap[preconditionKey];
            return precondition && precondition.callback(caseInfo);
        });
    };

    const allRequirementsMet = areAllRequirementsMet();
    const allRecommendationsMet = areAllRecommendationsMet();
    const requirementsStatus = getRequirementsStatus();

    const isDraftingInProgress = htmlContent?.length < 40 && htmlContent?.includes('Draft is being generated...');

    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
                {/* Left Sidebar - Drafts List */}
                <Grid item xs={12} md={3}>
                    <Box sx={{
                        backgroundColor: 'var(--background-color)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        overflow: 'hidden',
                        boxShadow: 'var(--shadow-light)',
                        padding: '16px'
                    }}>
                        <Grid container spacing={0} columns={3} sx={{ marginBottom: 2 }}>
                            <Grid item xs={1} sx={{ textAlign: "left" }}>
                                {selectedDraft && <Fab
                                    variant="extended"
                                    size="medium"
                                    color="primary"
                                    onClick={() => {
                                        setSelectedDraft(null);
                                        setHasUnsavedChanges(false);
                                    }}
                                    sx={{
                                        top: "-4px",
                                        background: 'var(--button-gradient)',
                                        boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '0.95rem',
                                        borderRadius: '12px',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        border: 'none',
                                        '&:hover': {
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)',
                                            background: 'var(--button-gradient-hover)'
                                        },
                                        '&:active': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)'
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: '-100%',
                                            width: '100%',
                                            height: '100%',
                                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                            transition: 'left 0.6s'
                                        },
                                        '&:hover::before': {
                                            left: '100%'
                                        }
                                    }}
                                >
                                    <AddIcon sx={{ mr: isSmallViewport ? 0 : 1 }} />
                                    {!isSmallViewport && 'New'}
                                </Fab>}
                            </Grid>
                            <Grid item xs={1}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        padding: "0 0 15px",
                                        color: 'var(--text-primary)',
                                        fontWeight: 500,
                                        fontSize: '1.3rem',
                                        letterSpacing: '-0.01em'
                                    }}
                                >
                                    My Drafts
                                </Typography>
                            </Grid>
                        </Grid>
                        {sortedDrafts.length === 0 ? (
                            <Box sx={{
                                padding: '40px 20px',
                                textAlign: 'center',
                                color: 'var(--text-secondary)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <DescriptionIcon sx={{ fontSize: '48px', opacity: 0.3, mb: 1 }} />
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    No drafts yet
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    Your drafts will appear here once you create them
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ padding: '0' }}>
                                {sortedDrafts.map((draft) => (
                                    <ListItem
                                        key={draft.filename}
                                        onClick={() => {
                                            setSelectedDraft(draft);
                                            setHasUnsavedChanges(false);
                                        }}
                                        className="case-item"
                                        sx={{
                                            padding: '0',
                                            backgroundColor: selectedDraft?.filename === draft.filename
                                                ? 'var(--selected-bg)'
                                                : 'transparent',
                                            '&:hover': {
                                                backgroundColor: selectedDraft?.filename === draft.filename
                                                    ? 'var(--surface-hover)'
                                                    : 'rgba(0, 0, 0, 0.04)'
                                            }
                                        }}
                                    >
                                        <ListItemButton>
                                            <DescriptionIcon className="icon-style" sx={{ mr: 1 }} />
                                            <ListItemText primary={draft.display_name} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                </Grid>

                {/* Right Side - Editor or Prompt */}
                <Grid item xs={12} md={9}>
                    {!selectedDraft ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '400px',
                            backgroundColor: 'var(--background-color)',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)',
                            boxShadow: 'var(--shadow-light)',
                            padding: '40px'
                        }}>
                            <Box sx={{ width: '100%', maxWidth: '500px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Typography
                                    variant={"h6"}
                                    sx={{
                                        color: 'var(--text-primary)',
                                        fontWeight: 600
                                    }}
                                >
                                    Select document type to draft
                                </Typography>
                                <Autocomplete
                                    value={selectedDraftType}
                                    onChange={(event, newValue) => {
                                        setSelectedDraftType(newValue);
                                    }}
                                    options={draftTypes}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => option.prompt === value.prompt}
                                    sx={{
                                        width: '100%',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            '& fieldset': {
                                                borderColor: 'var(--border-color)',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'var(--accent-color)',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'var(--accent-color)',
                                            },
                                        }
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Document Type" />}
                                />
                                {selectedDraftType && (
                                    <Box sx={{ width: '100%', mt: 3 }}>
                                        {/* Status Message */}
                                        {!allRequirementsMet && (
                                            <Box
                                                sx={{
                                                    mb: 3,
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                }}
                                            >
                                                <Warning sx={{ color: 'var(--error-color)', fontSize: '20px', flexShrink: 0, mt: 0.125 }} />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'var(--text-primary)',
                                                        fontSize: '1rem',
                                                        lineHeight: 1.6,
                                                        fontWeight: 400,
                                                        letterSpacing: 'normal',
                                                        flex: 1
                                                    }}
                                                >
                                                    {requirementsStatus.met}/{requirementsStatus.total} requirements met. Please fix the unmet requirements to generate {selectedDraftType.label}.
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Recommendation Message - shown when requirements are met but recommendations are not */}
                                        {allRequirementsMet && !allRecommendationsMet && (
                                            <Box
                                                sx={{
                                                    mb: 3,
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                }}
                                            >
                                                <Info sx={{ color: 'var(--info-color)', fontSize: '20px', flexShrink: 0, mt: 0.125 }} />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'var(--text-primary)',
                                                        fontSize: '1rem',
                                                        lineHeight: 1.6,
                                                        fontWeight: 400,
                                                        letterSpacing: 'normal',
                                                        flex: 1
                                                    }}
                                                >
                                                    See recommendations below to improve the quality of the draft.
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Requirements Section */}
                                        {(!allRequirementsMet || !allRecommendationsMet) && selectedDraftType.required_preconditions && selectedDraftType.required_preconditions.length > 0 && (
                                            <Box
                                                sx={{
                                                    backgroundColor: 'var(--surface-color)',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '8px',
                                                    padding: '20px',
                                                    mb: 3,
                                                    boxShadow: 'var(--shadow-subtle)'
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: 'var(--text-primary)',
                                                        fontWeight: 600,
                                                        mb: 2,
                                                        fontSize: '0.9rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Requirements
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {selectedDraftType.required_preconditions.map((preconditionKey) => {
                                                        const precondition = preconditionsMap[preconditionKey];
                                                        if (!precondition) return null;
                                                        const isMet = precondition.callback(caseInfo);
                                                        return (
                                                            <Box
                                                                key={preconditionKey}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'flex-start',
                                                                    textAlign: 'left',
                                                                    gap: 2
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        width: '24px',
                                                                        height: '24px',
                                                                        flexShrink: 0,
                                                                        mt: 0.125
                                                                    }}
                                                                >
                                                                    {isMet ? (
                                                                        <CheckCircle sx={{ color: 'var(--success-color)', fontSize: '24px' }} />
                                                                    ) : (
                                                                        <Cancel sx={{ color: 'var(--error-color)', fontSize: '24px' }} />
                                                                    )}
                                                                </Box>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: isMet ? 'var(--text-primary)' : 'var(--text-primary)',
                                                                        fontSize: '0.875rem',
                                                                        lineHeight: 1.6,
                                                                        flex: 1
                                                                    }}
                                                                >
                                                                    {precondition.description}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        )}
                                        {/* Recommendations Section */}
                                        {(!allRequirementsMet || !allRecommendationsMet) && selectedDraftType.optional_preconditions && selectedDraftType.optional_preconditions.length > 0 && (
                                            <Box
                                                sx={{
                                                    backgroundColor: 'var(--surface-color)',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '8px',
                                                    padding: '20px',
                                                    boxShadow: 'var(--shadow-subtle)'
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: 'var(--text-primary)',
                                                        fontWeight: 600,
                                                        mb: 2,
                                                        fontSize: '0.9rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Recommendations
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            color: 'var(--text-secondary)',
                                                            fontWeight: 400,
                                                            fontSize: '0.75rem',
                                                            ml: 1,
                                                            textTransform: 'none',
                                                            fontStyle: 'italic'
                                                        }}
                                                    >
                                                        (Optional)
                                                    </Typography>
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {selectedDraftType.optional_preconditions.map((preconditionKey) => {
                                                        const precondition = preconditionsMap[preconditionKey];
                                                        if (!precondition) return null;
                                                        const isMet = precondition.callback(caseInfo);
                                                        return (
                                                            <Box
                                                                key={preconditionKey}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'flex-start',
                                                                    textAlign: 'left',
                                                                    gap: 2
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        width: '24px',
                                                                        height: '24px',
                                                                        flexShrink: 0,
                                                                        mt: 0.125
                                                                    }}
                                                                >
                                                                    {isMet ? (
                                                                        <CheckCircle sx={{ color: 'var(--success-color)', fontSize: '24px' }} />
                                                                    ) : (
                                                                        <Info sx={{ color: 'var(--info-color)', fontSize: '24px' }} />
                                                                    )}
                                                                </Box>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: 'var(--text-primary)',
                                                                        fontSize: '0.875rem',
                                                                        lineHeight: 1.6,
                                                                        flex: 1
                                                                    }}
                                                                >
                                                                    {precondition.description}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                                <Button
                                    variant="contained"
                                    disabled={!allRequirementsMet}
                                    title={!allRequirementsMet ? "Please meet all requirements above before generating the draft" : "Click to generate the draft"}
                                    onClick={async () => {
                                        if (!selectedDraftType || !caseInfo?.caseId || !allRequirementsMet) {
                                            return;
                                        }
                                        const response = await fetch('/api/case/' + caseInfo['caseId'] + '/analysis_queue', {
                                            method: 'POST',
                                            credentials: 'same-origin',
                                            headers: new Headers({ 'content-type': 'application/json' }),
                                            body: JSON.stringify({
                                                analysis_queue: [selectedDraftType.prompt],
                                                item_type: 'drafting'
                                            })
                                        });
                                        console.log('Create draft button clicked: ' + response);

                                        if (response.ok) {
                                            // fetchCaseInfoAndUpdateUI to update display_name
                                            setTimeout(fetchCaseInfoAndUpdateUI, 10000);
                                            // Mimic new draft entry on client until server data loads.
                                            const responseData = await response.json();
                                            const newDraft = {
                                                filename: responseData.filename,
                                                display_name: responseData.display_name,
                                                analysis_id: responseData.analysis_id,
                                                last_updated: responseData.last_updated
                                            };
                                            const updatedDrafts = [...(caseInfo.drafts || []), newDraft];
                                            setCaseInfo({ ...caseInfo, drafts: updatedDrafts });
                                            setSelectedDraft(newDraft);
                                            setHasUnsavedChanges(false);
                                        }
                                    }}
                                    sx={{
                                        backgroundColor: allRequirementsMet ? 'transparent' : 'var(--text-secondary)',
                                        background: allRequirementsMet ? 'var(--button-gradient)' : 'var(--text-secondary)',
                                        color: 'white',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        padding: '12px 24px',
                                        borderRadius: '12px',
                                        cursor: allRequirementsMet ? 'pointer' : 'not-allowed',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        boxShadow: allRequirementsMet ? '0 4px 15px rgba(212, 175, 55, 0.4)' : 'none',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        border: 'none',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: '-100%',
                                            width: '100%',
                                            height: '100%',
                                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                            transition: 'left 0.6s'
                                        },
                                        '&:hover:not(:disabled)': {
                                            background: 'var(--button-gradient-hover)',
                                            boxShadow: '0 6px 20px rgba(212, 175, 55, 0.5)',
                                            transform: 'translateY(-2px)'
                                        },
                                        '&:hover:not(:disabled)::before': {
                                            left: '100%'
                                        },
                                        '&:disabled': {
                                            backgroundColor: 'var(--text-secondary)',
                                            background: 'var(--text-secondary)',
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            cursor: 'not-allowed',
                                            opacity: 0.6,
                                            boxShadow: 'none',
                                            border: '1px solid var(--border-color)',
                                            pointerEvents: 'auto'
                                        },
                                        '&:active:not(:disabled)': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)'
                                        }
                                    }}
                                    startIcon={<EditNote />}
                                >
                                    Create Draft
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            {(isDraftingInProgress || isLoading || !editor) && !error && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '400px',
                                    backgroundColor: 'var(--background-color)',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-color)',
                                    boxShadow: 'var(--shadow-light)'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <img
                                            src="/loading.gif"
                                            alt="loading..."
                                            title="Loading draft..."
                                            style={{
                                                height: "24px",
                                                verticalAlign: "middle"
                                            }}
                                        />
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'var(--text-secondary)',
                                                fontStyle: 'italic'
                                            }}
                                        >
                                            {isDraftingInProgress ? 'Draft is being generated...' : 'Loading draft document...'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {error && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '400px',
                                    backgroundColor: 'var(--background-color)',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-color)',
                                    boxShadow: 'var(--shadow-light)'
                                }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'var(--text-secondary)',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        {error}
                                    </Typography>
                                </Box>
                            )}

                            {!isDraftingInProgress && !isLoading && !error && editor && (
                                <Box sx={{
                                    backgroundColor: 'var(--background-color)',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-color)',
                                    overflow: 'hidden',
                                    boxShadow: 'var(--shadow-light)'
                                }}>
                                    {/* Toolbar */}
                                    <Box sx={{
                                        backgroundColor: 'var(--surface-color)',
                                        borderBottom: '1px solid var(--border-color)',
                                        padding: '8px 12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        flexWrap: 'wrap'
                                    }}>
                                        {/* Undo/Redo */}
                                        <ButtonGroup size="small" variant="text" sx={{ border: 'none' }}>
                                            <Tooltip title="Undo (Ctrl+Z)">
                                                <span>
                                                    <IconButton
                                                        onClick={() => editor.chain().focus().undo().run()}
                                                        disabled={!editor.can().chain().focus().undo().run()}
                                                        aria-label="Undo"
                                                        sx={{
                                                            color: 'var(--text-primary)',
                                                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                                            '&:disabled': { color: 'var(--text-secondary)', opacity: 0.5 }
                                                        }}
                                                    >
                                                        <UndoIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Redo (Ctrl+Y)">
                                                <span>
                                                    <IconButton
                                                        onClick={() => editor.chain().focus().redo().run()}
                                                        disabled={!editor.can().chain().focus().redo().run()}
                                                        aria-label="Redo"
                                                        sx={{
                                                            color: 'var(--text-primary)',
                                                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                                            '&:disabled': { color: 'var(--text-secondary)', opacity: 0.5 }
                                                        }}
                                                    >
                                                        <RedoIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </ButtonGroup>

                                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                                        {/* Text Formatting */}
                                        <ButtonGroup size="small" variant="text" sx={{ border: 'none' }}>
                                            <Tooltip title="Bold (Ctrl+B)">
                                                <IconButton
                                                    onClick={() => editor.chain().focus().toggleBold().run()}
                                                    aria-label="Bold"
                                                    sx={{
                                                        color: editor.isActive('bold') ? 'var(--accent-color)' : 'var(--text-primary)',
                                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                    }}
                                                >
                                                    <FormatBoldIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Italic (Ctrl+I)">
                                                <IconButton
                                                    onClick={() => editor.chain().focus().toggleItalic().run()}
                                                    aria-label="Italic"
                                                    sx={{
                                                        color: editor.isActive('italic') ? 'var(--accent-color)' : 'var(--text-primary)',
                                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                    }}
                                                >
                                                    <FormatItalicIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Underline (Ctrl+U)">
                                                <IconButton
                                                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                                                    aria-label="Underline"
                                                    sx={{
                                                        color: editor.isActive('underline') ? 'var(--accent-color)' : 'var(--text-primary)',
                                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                    }}
                                                >
                                                    <FormatUnderlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </ButtonGroup>

                                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                                        {/* Headings */}
                                        <Tooltip title="Text Style">
                                            <Select
                                                size="small"
                                                aria-label="Text Style"
                                                value={editor.isActive('heading', { level: 1 }) ? 'h1' :
                                                    editor.isActive('heading', { level: 2 }) ? 'h2' :
                                                        editor.isActive('heading', { level: 3 }) ? 'h3' :
                                                            editor.isActive('heading', { level: 4 }) ? 'h4' :
                                                                editor.isActive('heading', { level: 5 }) ? 'h5' :
                                                                    editor.isActive('heading', { level: 6 }) ? 'h6' : 'paragraph'}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === 'paragraph') {
                                                        editor.chain().focus().setParagraph().run();
                                                    } else {
                                                        const level = parseInt(value.replace('h', ''));
                                                        editor.chain().focus().toggleHeading({ level }).run();
                                                    }
                                                }}
                                                sx={{
                                                    minWidth: '8em',
                                                    height: '32px',
                                                    '& .MuiSelect-select': {
                                                        padding: '4px 8px',
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            >
                                                <MenuItem value="paragraph">Paragraph</MenuItem>
                                                <MenuItem value="h1">Heading 1</MenuItem>
                                                <MenuItem value="h2">Heading 2</MenuItem>
                                                <MenuItem value="h3">Heading 3</MenuItem>
                                                <MenuItem value="h4">Heading 4</MenuItem>
                                                <MenuItem value="h5">Heading 5</MenuItem>
                                                <MenuItem value="h6">Heading 6</MenuItem>
                                            </Select>
                                        </Tooltip>

                                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                                        {/* Lists */}
                                        <ButtonGroup size="small" variant="text" sx={{ border: 'none' }}>
                                            <Tooltip title="Bullet List">
                                                <IconButton
                                                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                                                    aria-label="Bullet List"
                                                    sx={{
                                                        color: editor.isActive('bulletList') ? 'var(--accent-color)' : 'var(--text-primary)',
                                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                    }}
                                                >
                                                    <FormatListBulletedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Numbered List">
                                                <IconButton
                                                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                                                    aria-label="Numbered List"
                                                    sx={{
                                                        color: editor.isActive('orderedList') ? 'var(--accent-color)' : 'var(--text-primary)',
                                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                    }}
                                                >
                                                    <FormatListNumberedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </ButtonGroup>

                                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                                        {/* Text Alignment */}
                                        <ButtonGroup size="small" variant="text" sx={{ border: 'none' }}>
                                            <Tooltip title="Align Left">
                                                <IconButton
                                                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                                                    aria-label="Align Left"
                                                    sx={{
                                                        color: editor.isActive({ textAlign: 'left' }) ? 'var(--accent-color)' : 'var(--text-primary)',
                                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                    }}
                                                >
                                                    <FormatAlignLeftIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Align Center">
                                                <IconButton
                                                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                                                    aria-label="Align Center"
                                                    sx={{
                                                        color: editor.isActive({ textAlign: 'center' }) ? 'var(--accent-color)' : 'var(--text-primary)',
                                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                    }}
                                                >
                                                    <FormatAlignCenterIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Align Right">
                                                <IconButton
                                                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                                                    aria-label="Align Right"
                                                    sx={{
                                                        color: editor.isActive({ textAlign: 'right' }) ? 'var(--accent-color)' : 'var(--text-primary)',
                                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                    }}
                                                >
                                                    <FormatAlignRightIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Justify">
                                                <IconButton
                                                    onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                                                    aria-label="Justify"
                                                    sx={{
                                                        color: editor.isActive({ textAlign: 'justify' }) ? 'var(--accent-color)' : 'var(--text-primary)',
                                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                    }}
                                                >
                                                    <FormatAlignJustifyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </ButtonGroup>

                                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                                        {/* Text Color */}
                                        <Tooltip title="Text Color">
                                            <IconButton
                                                component="label"
                                                aria-label="Text Color"
                                                sx={{
                                                    color: 'var(--text-primary)',
                                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                                    position: 'relative',
                                                }}
                                            >
                                                <TitleIcon fontSize="small" />
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: 4,
                                                        left: '50%',
                                                        transform: 'translateX(-50%)',
                                                        width: 12,
                                                        height: 3,
                                                        backgroundColor: editor.getAttributes('textStyle').color || 'var(--text-primary)',
                                                        borderRadius: 1,
                                                    }}
                                                />
                                                <input
                                                    type="color"
                                                    hidden
                                                    onChange={(e) => {
                                                        editor.chain().focus().setColor(e.target.value).run();
                                                    }}
                                                    value={editor.getAttributes('textStyle').color || '#000000'}
                                                    aria-label="Select text color"
                                                />
                                            </IconButton>
                                        </Tooltip>

                                        {/* Highlight */}
                                        <Tooltip title="Highlight">
                                            <IconButton
                                                onClick={() => {
                                                    if (editor.isActive('highlight')) {
                                                        editor.chain().focus().unsetHighlight().run();
                                                    } else {
                                                        editor.chain().focus().toggleHighlight({ color: '#ffeb3b' }).run();
                                                    }
                                                }}
                                                aria-label="Highlight"
                                                sx={{
                                                    color: editor.isActive('highlight') ? 'var(--accent-color)' : 'var(--text-primary)',
                                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                }}
                                            >
                                                <DriveFileRenameOutlineIcon />
                                            </IconButton>
                                        </Tooltip>

                                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                                        {/* Insert Table */}
                                        <Tooltip title="Insert Table">
                                            <IconButton
                                                onClick={(e) => {
                                                    setTableInsertPopover({
                                                        ...tableInsertPopover,
                                                        anchorEl: e.currentTarget
                                                    });
                                                }}
                                                disabled={editor.isActive('table')}
                                                aria-label="Insert Table"
                                                sx={{
                                                    color: 'var(--text-primary)',
                                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                                    '&:disabled': { color: 'var(--text-secondary)', opacity: 0.5 }
                                                }}
                                            >
                                                <GridOnIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        <Box sx={{ flexGrow: 1 }} />

                                        {/* Save Button */}
                                        <Tooltip title={hasUnsavedChanges ? "Save changes (Ctrl+S)" : "All changes saved"}>
                                            <span>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<SaveIcon />}
                                                    onClick={() => handleSave(true)}
                                                    disabled={isSaving || !hasUnsavedChanges}
                                                    aria-label={hasUnsavedChanges ? "Save changes" : "All changes saved"}
                                                    sx={{
                                                        backgroundColor: hasUnsavedChanges ? 'var(--accent-color)' : 'var(--text-secondary)',
                                                        color: 'white',
                                                        '&:hover': {
                                                            backgroundColor: hasUnsavedChanges ? 'var(--accent-color)' : 'var(--text-secondary)',
                                                            opacity: 0.9
                                                        },
                                                        '&:disabled': {
                                                            backgroundColor: 'var(--text-secondary)',
                                                            color: 'rgba(255, 255, 255, 0.7)',
                                                        },
                                                        textTransform: 'none'
                                                    }}
                                                >
                                                    {isSaving ? 'Saving...' : hasUnsavedChanges ? 'Save' : 'Saved'}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        <CopyToClipboardButton
                                            getContent={() => editor?.getHTML() || ''}
                                            setSnackBarMessage={setSnackBarMessage}
                                            buttonText="Copy Draft"
                                            contentType="draft"
                                        />
                                    </Box>

                                    {/* Editor Content */}
                                    <Box
                                        ref={editorScrollContainerRef}
                                        sx={{
                                            padding: '16px',
                                            minHeight: '400px',
                                            maxHeight: '70vh',
                                            overflowY: 'auto',
                                            '& .ProseMirror': {
                                                color: 'var(--text-primary)',
                                                textAlign: 'left',
                                                outline: 'none',
                                                minHeight: '400px',
                                                '& table': {
                                                    borderCollapse: 'collapse !important'
                                                },
                                                '& td, & th': {
                                                    border: '1px solid var(--border-color) !important',
                                                    padding: '0px 5px !important',
                                                    position: 'relative !important',
                                                },
                                                '& div.column-resize-handle': {
                                                    position: 'absolute !important',
                                                    right: '-2px !important',
                                                    top: '0 !important',
                                                    bottom: '0 !important',
                                                    width: '4px !important',
                                                    cursor: 'col-resize !important',
                                                    zIndex: 1,
                                                    pointerEvents: 'auto !important',
                                                },
                                                '& p': {
                                                    margin: '0.5em 0',
                                                },
                                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                                    color: 'var(--text-primary)',
                                                    marginTop: '1em',
                                                    marginBottom: '0.5em',
                                                    fontWeight: 600,
                                                },
                                                '& h1': { fontSize: '2em' },
                                                '& h2': { fontSize: '1.5em' },
                                                '& h3': { fontSize: '1.17em' },
                                                '& ul, & ol': {
                                                    paddingLeft: '1.5em',
                                                    margin: '0.5em 0',
                                                },
                                                '& a.citation': {
                                                    color: 'var(--text-muted)',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    fontSize: '0.9rem',
                                                    position: 'relative',
                                                    paddingLeft: '23px',
                                                    '&::before': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        left: '0',
                                                        top: '0.2em',
                                                        width: '14px',
                                                        height: '14px',
                                                        paddingLeft: '10px',
                                                        backgroundColor: 'currentColor',
                                                        maskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='20' viewBox='0 0 24 24' width='20'%3E%3Cpath d='M0 0h24v24H0z' fill='none'/%3E%3Cpath d='M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z' fill='black'/%3E%3C/svg%3E")`,
                                                        maskSize: 'contain',
                                                        maskRepeat: 'no-repeat',
                                                        maskPosition: 'center',
                                                        WebkitMaskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='20' viewBox='0 0 24 24' width='20'%3E%3Cpath d='M0 0h24v24H0z' fill='none'/%3E%3Cpath d='M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z' fill='black'/%3E%3C/svg%3E")`,
                                                        WebkitMaskSize: 'contain',
                                                        WebkitMaskRepeat: 'no-repeat',
                                                        WebkitMaskPosition: 'center',
                                                        transition: 'transform 0.2s ease',
                                                        display: 'inline-block',
                                                    },
                                                },
                                                '& a.citation:hover': {
                                                    color: 'var(--text-secondary)',
                                                    '&::before': {
                                                        transform: 'scale(1.5)',
                                                    },
                                                },
                                                '& a': {
                                                    color: 'var(--accent-color)',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    fontSize: '0.9rem',
                                                },
                                                '& mark': {
                                                    backgroundColor: '#ffeb3b',
                                                    padding: '2px 0',
                                                },
                                                '&:focus': {
                                                    outline: 'none',
                                                },
                                                // Warning span styles (with ::before badge on left and ::after dismiss button on right)
                                                '& .warning-needs-review': {
                                                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                                    padding: '2px 4px',
                                                    paddingLeft: '28px', // Space for the badge
                                                    paddingRight: '4px', // Small padding to prevent text from touching dismiss button
                                                    borderRadius: '3px',
                                                    borderLeft: '3px solid rgba(244, 67, 54, 0.3)',
                                                    position: 'relative',
                                                    '&::before': {
                                                        content: '"⚠"',
                                                        position: 'absolute',
                                                        left: '4px',
                                                        bottom: '0.1em',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '18px',
                                                        height: '18px',
                                                        borderRadius: '50%',
                                                        fontSize: '14px',
                                                        backgroundColor: 'rgba(244, 67, 54, 0.15)',
                                                        color: 'var(--error-color)',
                                                        border: '1px solid rgba(244, 67, 54, 0.3)',
                                                        cursor: 'pointer',
                                                        fontWeight: 'bold',
                                                        transition: 'all 0.2s ease',
                                                    },
                                                    '&:hover::before': {
                                                        backgroundColor: 'rgba(244, 67, 54, 0.25)',
                                                        transform: 'scale(1.5)',
                                                        boxShadow: '0 2px 4px rgba(244, 67, 54, 0.3)',
                                                    },
                                                    '&::after': {
                                                        content: '"×"',
                                                        position: 'absolute',
                                                        right: '-15px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '18px',
                                                        height: '18px',
                                                        borderRadius: '50%',
                                                        fontSize: '20px',
                                                        backgroundColor: 'rgba(244, 67, 54, 0.9)',
                                                        color: 'white',
                                                        cursor: 'pointer',
                                                        opacity: 0,
                                                        visibility: 'hidden',
                                                        transition: 'all 0.2s ease',
                                                        zIndex: 1,
                                                        fontWeight: 'bold',
                                                    },
                                                    '&:hover::after': {
                                                        opacity: 1,
                                                        visibility: 'visible',
                                                        transform: 'translateY(-50%)',
                                                        boxShadow: '0 2px 4px rgba(244, 67, 54, 0.4)',
                                                    },
                                                },
                                                '& .warning-caveat': {
                                                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                    padding: '2px 4px',
                                                    paddingLeft: '28px', // Space for the badge
                                                    paddingRight: '4px', // Small padding to prevent text from touching dismiss button
                                                    borderRadius: '3px',
                                                    borderLeft: '3px solid rgba(33, 150, 243, 0.3)',
                                                    position: 'relative',
                                                    '&::before': {
                                                        content: '"ℹ"',
                                                        position: 'absolute',
                                                        left: '4px',
                                                        bottom: '0.1em',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '18px',
                                                        height: '18px',
                                                        borderRadius: '50%',
                                                        fontSize: '14px',
                                                        backgroundColor: 'rgba(33, 150, 243, 0.15)',
                                                        color: 'var(--info-color)',
                                                        border: '1px solid rgba(33, 150, 243, 0.3)',
                                                        cursor: 'pointer',
                                                        fontWeight: 'bold',
                                                        transition: 'all 0.2s ease',
                                                    },
                                                    '&:hover::before': {
                                                        backgroundColor: 'rgba(33, 150, 243, 0.25)',
                                                        transform: 'scale(1.5)',
                                                        boxShadow: '0 2px 4px rgba(33, 150, 243, 0.3)',
                                                    },
                                                    '&::after': {
                                                        content: '"×"',
                                                        position: 'absolute',
                                                        right: '-15px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '18px',
                                                        height: '18px',
                                                        borderRadius: '50%',
                                                        fontSize: '20px',
                                                        lineHeight: '18px',
                                                        backgroundColor: 'rgba(33, 150, 243, 0.9)',
                                                        color: 'white',
                                                        cursor: 'pointer',
                                                        opacity: 0,
                                                        visibility: 'hidden',
                                                        transition: 'all 0.2s ease',
                                                        zIndex: 1,
                                                        fontWeight: 'bold',
                                                    },
                                                    '&:hover::after': {
                                                        opacity: 1,
                                                        visibility: 'visible',
                                                        transform: 'translateY(-50%)',
                                                        boxShadow: '0 2px 4px rgba(33, 150, 243, 0.4)',
                                                    },
                                                },
                                            }
                                        }}>
                                        <EditorContent editor={editor} />
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </Grid>
            </Grid>

            {/* Warning Message Popover */}
            <Popover
                open={Boolean(warningPopover.anchorEl)}
                anchorEl={warningPopover.anchorEl}
                onClose={() => {
                    // Restore scroll position before closing to prevent unwanted scrolling
                    if (warningPopover.scrollPosition && editorScrollContainerRef.current) {
                        // Use requestAnimationFrame to ensure DOM is ready, then restore scroll
                        requestAnimationFrame(() => {
                            if (editorScrollContainerRef.current) {
                                editorScrollContainerRef.current.scrollTop = warningPopover.scrollPosition.scrollTop;
                                editorScrollContainerRef.current.scrollLeft = warningPopover.scrollPosition.scrollLeft;
                            }
                        });
                    }
                    setWarningPopover({ anchorEl: null, message: '', type: null, scrollPosition: null });
                }}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            maxWidth: '400px',
                            borderRadius: '4px',
                            boxShadow: 'var(--shadow-medium)',
                            border: `1px solid ${warningPopover.type === 'needs_review'
                                ? 'rgba(244, 67, 54, 0.2)'
                                : 'rgba(33, 150, 243, 0.2)'}`,
                        }
                    }
                }}
            >
                <Box sx={{
                    padding: '4px 6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    backgroundColor: warningPopover.type === 'needs_review'
                        ? 'rgba(244, 67, 54, 0.05)'
                        : 'rgba(33, 150, 243, 0.05)',
                }}>
                    {warningPopover.type === 'needs_review' ? (
                        <Warning sx={{
                            color: 'var(--error-color)',
                            fontSize: '14px',
                            flexShrink: 0
                        }} />
                    ) : (
                        <Info sx={{
                            color: 'var(--info-color)',
                            fontSize: '14px',
                            flexShrink: 0
                        }} />
                    )}
                    <Typography sx={{
                        color: warningPopover.type === 'needs_review'
                            ? 'var(--error-color)'
                            : 'var(--info-color)',
                        fontSize: '12px',
                        fontWeight: 500,
                        flex: 1,
                        lineHeight: 1.3,
                    }}>
                        {warningPopover.message}
                    </Typography>
                </Box>
            </Popover>

            {/* Table Context Menu */}
            <Menu
                open={tableContextMenu.mouseY !== null}
                onClose={() => {
                    setTableContextMenu({
                        anchorEl: null,
                        mouseX: null,
                        mouseY: null
                    });
                }}
                anchorReference="anchorPosition"
                anchorPosition={
                    tableContextMenu.mouseY !== null && tableContextMenu.mouseX !== null
                        ? { top: tableContextMenu.mouseY, left: tableContextMenu.mouseX }
                        : undefined
                }
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            minWidth: 200,
                            borderRadius: '4px',
                            boxShadow: 'var(--shadow-medium)',
                            border: '1px solid var(--border-color)',
                            '& .MuiMenuItem-root': {
                                fontSize: '0.875rem',
                                padding: '8px 16px',
                                gap: 1.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                },
                                '&.Mui-disabled': {
                                    opacity: 0.5
                                }
                            }
                        }
                    }
                }}
            >
                {/* Row Operations */}
                <MenuItem
                    onClick={() => {
                        editor.chain().focus().addRowBefore().run();
                        setTableContextMenu({
                            anchorEl: null,
                            mouseX: null,
                            mouseY: null
                        });
                    }}
                    disabled={!editor.can().addRowBefore()}
                >
                    <ListItemIcon>
                        <ArrowUpwardIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Add Row Above</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        editor.chain().focus().addRowAfter().run();
                        setTableContextMenu({
                            anchorEl: null,
                            mouseX: null,
                            mouseY: null
                        });
                    }}
                    disabled={!editor.can().addRowAfter()}
                >
                    <ListItemIcon>
                        <ArrowDownwardIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Add Row Below</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        editor.chain().focus().deleteRow().run();
                        setTableContextMenu({
                            anchorEl: null,
                            mouseX: null,
                            mouseY: null
                        });
                    }}
                    disabled={!editor.can().deleteRow()}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Delete Row</ListItemText>
                </MenuItem>

                <Divider />

                {/* Column Operations */}
                <MenuItem
                    onClick={() => {
                        editor.chain().focus().addColumnBefore().run();
                        setTableContextMenu({
                            anchorEl: null,
                            mouseX: null,
                            mouseY: null
                        });
                    }}
                    disabled={!editor.can().addColumnBefore()}
                >
                    <ListItemIcon>
                        <ArrowBackIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Add Column Left</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        editor.chain().focus().addColumnAfter().run();
                        setTableContextMenu({
                            anchorEl: null,
                            mouseX: null,
                            mouseY: null
                        });
                    }}
                    disabled={!editor.can().addColumnAfter()}
                >
                    <ListItemIcon>
                        <ArrowForwardIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Add Column Right</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        editor.chain().focus().deleteColumn().run();
                        setTableContextMenu({
                            anchorEl: null,
                            mouseX: null,
                            mouseY: null
                        });
                    }}
                    disabled={!editor.can().deleteColumn()}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Delete Column</ListItemText>
                </MenuItem>

                <Divider />

                {/* Table Operations */}
                <MenuItem
                    onClick={() => {
                        if (window.confirm('Are you sure you want to delete this table?')) {
                            editor.chain().focus().deleteTable().run();
                        }
                        setTableContextMenu({
                            anchorEl: null,
                            mouseX: null,
                            mouseY: null
                        });
                    }}
                    disabled={!editor.can().deleteTable()}
                    sx={{
                        color: 'var(--error-color)',
                        '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.08)'
                        }
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{ color: 'var(--error-color)' }} />
                    </ListItemIcon>
                    <ListItemText>Delete Table</ListItemText>
                </MenuItem>
            </Menu>

            {/* Table Insert Popover */}
            <Popover
                open={Boolean(tableInsertPopover.anchorEl)}
                anchorEl={tableInsertPopover.anchorEl}
                onClose={() => {
                    setTableInsertPopover({
                        ...tableInsertPopover,
                        anchorEl: null
                    });
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            p: 2,
                            borderRadius: '4px',
                            boxShadow: 'var(--shadow-medium)',
                        }
                    }
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: '280px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Insert Table
                    </Typography>

                    {/* Grid Size Selector */}
                    <Box>
                        <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'var(--text-secondary)' }}>
                            Select table size:
                        </Typography>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(10, 1fr)',
                            gap: 0.5,
                            border: '1px solid var(--border-color)',
                            p: 0.5,
                            borderRadius: 1
                        }}>
                            {Array.from({ length: 100 }, (_, i) => {
                                const row = Math.floor(i / 10) + 1;
                                const col = (i % 10) + 1;
                                const isSelected = row <= tableInsertPopover.selectedRows && col <= tableInsertPopover.selectedCols;

                                return (
                                    <Box
                                        key={i}
                                        onMouseEnter={() => {
                                            setTableInsertPopover(prev => ({
                                                ...prev,
                                                selectedRows: row,
                                                selectedCols: col
                                            }));
                                        }}
                                        onClick={() => {
                                            editor.chain().focus().insertTable({
                                                rows: row,
                                                cols: col,
                                                withHeaderRow: tableInsertPopover.withHeaderRow
                                            }).run();
                                            setTableInsertPopover({
                                                ...tableInsertPopover,
                                                anchorEl: null
                                            });
                                        }}
                                        sx={{
                                            width: '20px',
                                            height: '20px',
                                            border: '1px solid var(--border-color)',
                                            backgroundColor: isSelected ? 'var(--accent-color)' : 'transparent',
                                            opacity: isSelected ? 0.6 : 0.3,
                                            cursor: 'pointer',
                                            transition: 'all 0.1s ease',
                                            '&:hover': {
                                                backgroundColor: isSelected ? 'var(--accent-color)' : 'rgba(0, 0, 0, 0.1)',
                                                opacity: 1,
                                                borderColor: 'var(--accent-color)'
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Box>
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'var(--text-secondary)' }}>
                            {tableInsertPopover.selectedRows} × {tableInsertPopover.selectedCols} table
                        </Typography>
                    </Box>

                    {/* Header Row Toggle */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                            type="checkbox"
                            id="table-header-row"
                            checked={tableInsertPopover.withHeaderRow}
                            onChange={(e) => {
                                setTableInsertPopover(prev => ({
                                    ...prev,
                                    withHeaderRow: e.target.checked
                                }));
                            }}
                            style={{ cursor: 'pointer' }}
                        />
                        <label
                            htmlFor="table-header-row"
                            style={{
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                color: 'var(--text-primary)'
                            }}
                        >
                            Header row
                        </label>
                    </Box>

                    {/* Insert Button */}
                    <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={() => {
                            editor.chain().focus().insertTable({
                                rows: tableInsertPopover.selectedRows,
                                cols: tableInsertPopover.selectedCols,
                                withHeaderRow: tableInsertPopover.withHeaderRow
                            }).run();
                            setTableInsertPopover({
                                ...tableInsertPopover,
                                anchorEl: null
                            });
                        }}
                        sx={{
                            backgroundColor: 'var(--accent-color)',
                            color: 'white',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'var(--accent-color)',
                                opacity: 0.9
                            }
                        }}
                    >
                        Insert Table
                    </Button>
                </Box>
            </Popover>
        </Box>
    );
}
